import { AlertTriangle, CalendarClock, CheckCircle2, Pause, Rocket, XCircle } from 'lucide-react'

import { CAMPAIGN_STATUS_LABELS, canPublish, describeCampaignWindow } from '@iws/theme-schema/campaign'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { CampaignBuilderState } from '@/hooks/features/campaigns'

import { CAMPAIGN_STATUS_TONES } from '../CampaignsTable/status-styles'

import { ShareKit } from './ShareKit'

const PROMOTIONS = [
  { key: 'homeBanner', label: 'Home page banner', help: 'A banner at the top of your home page while the campaign is live.' },
  { key: 'productBadge', label: 'Product badges and offer boxes', help: 'A badge on product cards, an offer box on product pages and hints in the cart.' },
  { key: 'announcementBar', label: 'Announcement bar', help: 'A thin bar above the header on every page.' },
] as const

/** Last step: pre-launch checklist (FR-CB8), where to promote, publish or schedule, and the sharing kit. */
export const LaunchStep = ({ ctx }: { ctx: CampaignBuilderState }) => {
  const promote = { homeBanner: false, productBadge: true, announcementBar: false, ...ctx.step3.ui_content.promote }
  const ready = canPublish(ctx.checklist)
  const startsLater = Date.parse(ctx.step1.starts_at) > Date.now()
  const running = ctx.status === 'live' || ctx.status === 'scheduled'
  const hero = ctx.step3.ui_content.blocks?.find((b) => b.type === 'hero')

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
      <Card role="region" aria-labelledby="checklist-title">
        <CardHeader>
          <CardTitle id="checklist-title">Before you launch</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="space-y-2">
            {ctx.checklist.map((item) => {
              const Icon = item.ok ? CheckCircle2 : item.level === 'error' ? XCircle : AlertTriangle
              return (
                <li key={item.id} className="flex gap-2 text-sm text-ink">
                  <Icon
                    className={cn('mt-0.5 size-4 shrink-0', item.ok ? 'text-success-dark' : item.level === 'error' ? 'text-error' : 'text-warning-dark')}
                    aria-hidden
                  />
                  <span>
                    {item.label}
                    <span className="sr-only">{item.ok ? ' — done' : item.level === 'error' ? ' — must fix' : ' — recommended'}</span>
                    {item.detail && <span className="block text-sm text-text-2">{item.detail}</span>}
                    {item.id === 'mobile' && !item.ok && (
                      <button type="button" className="ml-2 text-sm text-info-dark hover:underline" onClick={() => ctx.goTo('page')}>Open the phone preview</button>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
          {ctx.catalog.loading && <p className="text-sm text-muted-foreground">Checking stock…</p>}
        </CardContent>
      </Card>

      <Card role="region" aria-labelledby="promote-title">
        <CardHeader>
          <CardTitle id="promote-title">Where to promote it</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {PROMOTIONS.map((p) => (
            <div key={p.key} className="flex items-start gap-3">
              <Switch
                id={`promote-${p.key}`}
                checked={promote[p.key]}
                onCheckedChange={(v) => ctx.setPromotion({ [p.key]: v })}
              />
              <div className="space-y-0.5">
                <Label htmlFor={`promote-${p.key}`}>{p.label}</Label>
                <p className="text-sm text-text-2">{p.help}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card role="region" aria-labelledby="publish-title" className="lg:col-span-2">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle id="publish-title" className="flex items-center gap-2">
              Status <Badge tone={CAMPAIGN_STATUS_TONES[ctx.status]}>{CAMPAIGN_STATUS_LABELS[ctx.status]}</Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">{describeCampaignWindow(ctx.step1.starts_at, ctx.step1.ends_at)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {running && (
              <Button variant="outline" onClick={ctx.pause} disabled={ctx.submitLoading}>
                <Pause /> Pause
              </Button>
            )}
            {startsLater && (
              <Button variant="secondary" onClick={() => ctx.publish('schedule')} disabled={ctx.submitLoading || !ready}>
                <CalendarClock /> {ctx.status === 'scheduled' ? 'Update schedule' : 'Schedule'}
              </Button>
            )}
            <Button onClick={() => ctx.publish('now')} disabled={ctx.submitLoading || !ready || ctx.status === 'ended'}>
              <Rocket /> {ctx.status === 'live' ? 'Update live campaign' : 'Publish now'}
            </Button>
          </div>
        </CardHeader>
        {(!ready || ctx.status === 'ended') && (
          <CardContent className="space-y-1">
            {!ready && <p className="text-sm text-error">Fix the items marked with ✕ to publish.</p>}
            {ctx.status === 'ended' && <p className="text-sm text-muted-foreground">The end date has passed. Move it later on the Offer step to run this campaign again.</p>}
          </CardContent>
        )}
      </Card>

      {ctx.savedOffer?.slug && ctx.domain && (
        <Card role="region" aria-labelledby="share-title" className="lg:col-span-2">
          <CardHeader>
            <CardTitle id="share-title">Share it</CardTitle>
            {!running && <CardDescription>Links work once the campaign is live. Until then they show the “offer ended” page.</CardDescription>}
          </CardHeader>
          <CardContent>
            <ShareKit
              domain={ctx.domain}
              slug={ctx.savedOffer.slug}
              title={(hero?.props.title as string | undefined) || ctx.step3.ui_content.page_title || ctx.step1.name}
              subtitle={(hero?.props.badge_text as string | undefined) || ctx.step3.ui_content.page_description || ''}
              heroImage={(hero?.props.image as string | undefined) || ctx.step3.ui_content.hero_image}
              accent={ctx.step3.ui_content.accent_color}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
